// Authentication Manager for Parent Application
import { AuthTokens, AuthUser, OAuthTokenClaims } from './types';
import { cognitoConfig, getOAuthUrls } from './config';

const TOKEN_STORAGE_KEY = 'auth_tokens';
const USER_STORAGE_KEY = 'auth_user';

export class AuthManager {
  private tokens: AuthTokens | null = null;
  private user: AuthUser | null = null;
  private refreshTimer: number | null = null;

  constructor() {
    this.initialize();
  }

  async initialize(): Promise<void> {
    try {
      const storedTokens = this.getStoredTokens();
      const storedUser = this.getStoredUser();

      if (storedTokens && storedUser) {
        if (this.isTokenExpired(storedTokens.accessToken)) {
          await this.refreshTokens();
        } else {
          this.tokens = storedTokens;
          this.user = storedUser;
          this.setupAutoRefresh();
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.clearAuth();
    }
  }

  async login(): Promise<void> {
    const urls = getOAuthUrls();
    const state = this.generateRandomState();
    
    sessionStorage.setItem('oauth_state', state);
    
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: cognitoConfig.clientId,
      redirect_uri: urls.redirectUri,
      scope: 'openid profile email',
      state: state
    });

    window.location.href = `${urls.login}?${params.toString()}`;
  }

  async handleCallback(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (!code || !state) {
      throw new Error('Missing authorization code or state');
    }

    const storedState = sessionStorage.getItem('oauth_state');
    if (storedState !== state) {
      throw new Error('Invalid OAuth state');
    }
    sessionStorage.removeItem('oauth_state');

    const tokens = await this.exchangeCodeForTokens(code);
    const user = this.extractUserFromToken(tokens.idToken);

    if (!user) {
      throw new Error('Failed to extract user from token');
    }

    this.tokens = tokens;
    this.user = user;
    this.storeTokens(tokens);
    this.storeUser(user);
    this.setupAutoRefresh();

    // Clean up URL without full page reload
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  async logout(): Promise<void> {
    this.clearAuth();
    const urls = getOAuthUrls();
    const params = new URLSearchParams({
      client_id: cognitoConfig.clientId,
      logout_uri: urls.logoutUri
    });
    window.location.href = `${urls.logout}?${params.toString()}`;
  }

  async refreshTokens(): Promise<boolean> {
    if (!this.tokens?.refreshToken) {
      return false;
    }

    try {
      const urls = getOAuthUrls();
      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: cognitoConfig.clientId,
        refresh_token: this.tokens.refreshToken
      });

      const response = await fetch(urls.token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();
      const newTokens: AuthTokens = {
        accessToken: data.access_token,
        idToken: data.id_token,
        refreshToken: this.tokens.refreshToken
      };

      const newUser = this.extractUserFromToken(newTokens.idToken);
      if (!newUser) {
        throw new Error('Failed to extract user from refreshed token');
      }

      this.tokens = newTokens;
      this.user = newUser;
      this.storeTokens(newTokens);
      this.storeUser(newUser);
      this.setupAutoRefresh();

      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuth();
      return false;
    }
  }

  getAccessToken(): string | null {
    // Return ID token for AgentCore authentication
    // ID tokens have the client_id in the 'aud' claim which matches AgentCore's allowed_audience
    return this.tokens?.idToken || null;
  }

  getUser(): AuthUser | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.tokens && !this.isTokenExpired(this.tokens.accessToken);
  }

  private async exchangeCodeForTokens(code: string): Promise<AuthTokens> {
    const urls = getOAuthUrls();
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cognitoConfig.clientId,
      code: code,
      redirect_uri: urls.redirectUri
    });

    const response = await fetch(urls.token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const data = await response.json();
    return {
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: data.refresh_token
    };
  }

  private decodeToken(token: string): OAuthTokenClaims | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  private extractUserFromToken(idToken: string): AuthUser | null {
    const claims = this.decodeToken(idToken);
    if (!claims || !claims.sub) return null;

    return {
      id: claims.sub,
      email: claims.email,
      username: claims['cognito:username'],
      emailVerified: claims.email_verified
    };
  }

  private isTokenExpired(token: string): boolean {
    const claims = this.decodeToken(token);
    if (!claims || !claims.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return claims.exp < currentTime;
  }

  private setupAutoRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.tokens?.accessToken) return;

    const claims = this.decodeToken(this.tokens.accessToken);
    if (!claims || !claims.exp) return;

    const expirationTime = claims.exp * 1000;
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    
    // Refresh 5 minutes before expiration
    const refreshTime = Math.max(0, timeUntilExpiration - 5 * 60 * 1000);
    
    this.refreshTimer = window.setTimeout(() => {
      this.refreshTokens();
    }, refreshTime);
  }

  private storeTokens(tokens: AuthTokens): void {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  }

  private getStoredTokens(): AuthTokens | null {
    try {
      const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private storeUser(user: AuthUser): void {
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  }

  private getStoredUser(): AuthUser | null {
    try {
      const stored = sessionStorage.getItem(USER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private clearAuth(): void {
    this.tokens = null;
    this.user = null;
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(USER_STORAGE_KEY);
    sessionStorage.removeItem('oauth_state');
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private generateRandomState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
}
